import type {
  Data,
  Job,
  JobSpec,
  JobSpecInternalAccessIsolation,
  JobSpecPost,
} from "@eai-toolkit/client";
import { JobState } from "@eai-toolkit/client";
import type { Theme } from "@mui/material";
import shellQuote from "shell-quote";

import { UUID_V4_PATTERN } from "@helpers/uuid";

import { lightenWithRgba } from "../../utils/colorUtils";
import { generateFriendlyName } from "../../utils/friendlyName";

type Resource = object & {
  id: string;
};
export function isResource(o: unknown): o is Resource {
  return typeof o === "object" && o !== null && "id" in o;
}

// for state diffs in development mode
type ChangeCompareObject = { [key: string]: unknown };
type ObjectChanges = {
  [key: string]:
    | ObjectChanges
    | {
        old: unknown;
        new: unknown;
      };
};
export function objectChanges(
  oldObject: ChangeCompareObject,
  newObject: ChangeCompareObject,
) {
  const changes: ObjectChanges = {};
  for (const key of new Set(
    Object.keys(oldObject).concat(Object.keys(newObject)),
  )) {
    const oldValue = key in oldObject ? oldObject[key] : undefined;
    const newValue = newObject[key];
    if (oldValue === newValue) {
      continue;
    }
    if (
      !(oldValue instanceof Object && newValue instanceof Object) ||
      !(
        (oldValue.constructor === Object && newValue.constructor === Object) ||
        (oldValue.constructor === Array && newValue.constructor === Array)
      )
    ) {
      changes[key] = {
        old: oldValue,
        new: newValue,
      };
      continue;
    }
    const changesForKey = objectChanges(
      oldValue as ChangeCompareObject,
      newValue as ChangeCompareObject,
    );
    if (Object.keys(changesForKey).length) {
      changes[key] = changesForKey;
    }
  }
  return changes;
}

export function getStateBackgroundColor(
  state: string,
  palette: Theme["palette"],
) {
  switch (state) {
    case "CANCELLED":
      return palette.grey[600];
    case "CANCELLING":
      return palette.grey[200];
    case "FAILED":
      return palette.error.light;
    case "INTERRUPTED":
      return lightenWithRgba(palette.error.light);
    case "QUEUED":
      return palette.info.light;
    case "QUEUING":
      return lightenWithRgba(palette.info.light);
    case "RUNNING":
      return lightenWithRgba(palette.success.light);
    case "SUCCEEDED":
      return palette.success.light;
    default:
      return palette.text.secondary;
  }
}

export function getStateTextColor(state: string, palette: Theme["palette"]) {
  switch (state) {
    case "CANCELLING":
      return palette.grey[800];
    case "INTERRUPTED":
      return palette.error.dark;
    case "QUEUING":
      return palette.info.dark;
    case "RUNNING":
      return palette.success.dark;
    default:
      return palette.common.white;
  }
}

export function areSearchesEqual(searchA: string, searchB: string) {
  if (searchA.length !== searchB.length) {
    return false;
  }
  // remove '?' and split into key=value pairs
  const setA = new Set(searchA.slice(1).split("&"));
  const setB = new Set(searchB.slice(1).split("&"));
  return (
    setA.size === setB.size &&
    [...setA].every((paramPair) => setB.has(paramPair))
  );
}

export function timeout(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type DockerImage = {
  registry: string;
  repository: string;
  tag: string;
};

export function parseDockerImage(image: string): DockerImage {
  const firstSlashIndex = image.indexOf("/");
  const beforeSlash =
    firstSlashIndex === -1 ? undefined : image.slice(0, firstSlashIndex);
  // the before slash portion should be parsed as a registry if it contains '.'
  // or ':' but otherwise it's part of the image repository name
  // https://stackoverflow.com/a/37867949/4956731
  const registry =
    beforeSlash && /[.:]/.test(beforeSlash)
      ? beforeSlash
      : // empty registry means the image is from Docker Hub
        "";
  const [repository, tag] = image
    .slice(registry ? registry.length + 1 : 0)
    .split(":");
  return {
    registry,
    repository,
    tag,
  };
}

export function stringifyDockerImage(image: DockerImage) {
  const { registry, repository, tag } = image;
  const localImageName = tag ? `${repository}:${tag}` : repository;
  return registry ? `${registry}/${localImageName}` : localImageName;
}

export function validateImage(image: string | undefined) {
  // we don't check for a tag at submit time although we will require them if
  // the dropdown selection method is used for choosing the registry, repository
  // and tag individually, as specifying the image without a tag should be
  // soemthing done intentionally. if the user chooses the "manually paste
  // image" option then the tag requirement can be bypassed.
  if (!image || !parseDockerImage(image).repository) {
    return "Image must be specified";
  }
}

export const pathHelpText = "Path must be absolute";

function isInvalidPath(pathname: string): boolean {
  // Basic validation for invalid characters in paths
  const invalidChars = /[<>:"|?*]/;
  return invalidChars.test(pathname);
}

function validateAbsolutePath(pathname: string) {
  if (isInvalidPath(pathname)) {
    return "Invalid path";
  }
  if (!pathname.startsWith("/")) {
    return pathHelpText;
  }
}

export function validateMountPath(mountPath: string) {
  if (!mountPath) {
    return "Mount path must be defined";
  }
  if (mountPath === "/") {
    return "Mount path cannot be the root directory";
  }
  return validateAbsolutePath(mountPath);
}

export function validateSourcePath(sourcePath: string) {
  // Source path is optional (empty == root of the data object).
  if (!sourcePath) {
    return;
  }
  return validateAbsolutePath(sourcePath);
}

export function validateWorkDir(workDir: string | undefined) {
  if (!workDir) {
    return;
  }
  return validateAbsolutePath(workDir);
}

export function validateEnvironmentVarName(varName: string) {
  if (!varName) {
    return "Name cannot be empty";
  }
  if (!/^[A-Z0-9_]+$/.test(varName)) {
    return "Alphanumeric and underscores only";
  }
  if (/^[0-9]/.test(varName)) {
    return "No leading digits in name";
  }
}

export const commandHelpText = "A valid Docker container command is required";

function parseCommand(command: string) {
  // we'll parse the command in such a way as to display any variables specified
  // by the user, but we need to forbid this for submission since Docker won't
  // interpolate them directly (the command will need to be sent to a shell)
  let hasShellVar = false;
  const parsedCommand = shellQuote.parse(command, (varName) => {
    hasShellVar = true;
    // our "replacement" preserves the variables instead of interpolating them
    return `$${varName}`;
  });
  return {
    parsedCommand,
    hasShellVar,
  };
}

export function validateCommand(command: string | undefined) {
  if (!command) {
    return;
  }
  const { parsedCommand, hasShellVar } = parseCommand(command);
  if (hasShellVar || parsedCommand.find((arg) => typeof arg !== "string")) {
    return commandHelpText;
  }
}

export function validateJobName(jobName: string | undefined) {
  if (!jobName) {
    return;
  }
  if (!/^[a-z0-9_]+$/.test(jobName)) {
    return "Alphanumeric and underscores only";
  }
}

// port/targetPort allow "" so empty rows can exist in Redux mid-edit;
// tentativePayloadToJobSpec filters them out before sending to the backend.
export type DnsPort = {
  port: number | "";
  targetPort?: number | "";
  protocol?: "TCP" | "UDP";
};

export type DnsConfig = {
  name: string;
  ports: DnsPort[];
};

// Backend stores the full DNS label under k8s RFC1035 rules (max 63 chars,
// must start with a lowercase letter). After prefixing `dns-<UUID>-` (4 + 36
// + 1 = 41 chars), 22 remain for the user-supplied name. Replica jobs reserve
// extra room for a `-<rank>` suffix, so high replica counts can still overflow
// the 63-char limit; backend catches that.
export const DNS_NAME_MAX_LENGTH = 22;
const DNS_NAME_REGEX = /^[a-z]([a-z0-9-]{0,20}[a-z0-9])?$/;

// Backend prepends a UUID-based prefix to user-supplied DNS names; shape
// differs for replicas:
//   single  (replicas <= 1): `dns-<accountId>-<name>`
//   replica with name:       `dns-<ReplicaGroupID>-<name>-<rank>`
//   replica without name:    `dns-<ReplicaGroupID>-<rank>`
// Both IDs are UUID v4, so the regexes anchor on the UUID shape — no runtime
// ID needed. Backend: `internal/app/normalize_job_spec.go`.
const DNS_PREFIX_REGEX = new RegExp(`^dns-${UUID_V4_PATTERN}-`);
const DNS_REPLICA_REGEX = new RegExp(`^dns-${UUID_V4_PATTERN}-(?:(.+)-)?\\d+$`);

/** Whether `resources.replicas` makes this a replica job rather than a single job. */
export const isReplicaJob = (replicas: number | undefined): boolean =>
  (replicas ?? 0) > 1;

/** Reverse the backend's DNS prefix so the form shows the user's short name. */
function stripDnsPrefix(rawName: string, replicas: number | undefined): string {
  if (!rawName) return "";
  if (isReplicaJob(replicas)) {
    const match = rawName.match(DNS_REPLICA_REGEX);
    return match ? (match[1] ?? "") : rawName;
  }
  return rawName.replace(DNS_PREFIX_REGEX, "");
}

export function validateDnsName(name: string | undefined): string | null {
  if (!name) return null;
  if (name.length > DNS_NAME_MAX_LENGTH) {
    return `Name must be ${DNS_NAME_MAX_LENGTH} characters or fewer`;
  }
  if (!DNS_NAME_REGEX.test(name)) {
    return "Must start with a lowercase letter; otherwise lowercase letters, numbers, and dashes only (no trailing dash)";
  }
  return null;
}

// Keep DEFAULT_ACCESS_ISOLATION in sync with the backend's default, which
// is what an unset value resolves to for jobs not created through this form.

// Unlike other form defaults, which are shown in the UI but omitted from
// the payload when untouched, this value is always sent. It is an
// access-control setting, so what the user sees selected must be exactly
// what the job runs with, even if the backend default later changes.
export const DEFAULT_ACCESS_ISOLATION: JobSpecInternalAccessIsolation =
  "account";

/** The isolation the job runs with. Used for both display and submit so
 * the two can't disagree. */
export const resolveAccessIsolation = (
  spec: TentativeJobSpec,
): JobSpecInternalAccessIsolation =>
  spec.internalAccessIsolation ?? DEFAULT_ACCESS_ISOLATION;

export type TentativeJobSpec = Partial<
  Omit<JobSpec, "command"> & {
    command?: string;
    internalDns?: DnsConfig;
  }
> & {
  randomlyGeneratedName: string;
};

export function tentativePayloadToJobSpec(
  tentativePayload: TentativeJobSpec,
  dataInstances: Map<string, Data>,
): Partial<JobSpecPost> {
  const { data, command, name, randomlyGeneratedName, internalDns, ...rest } =
    tentativePayload;
  const jobSpec: Partial<JobSpecPost> = {
    ...rest,
    data: data?.map((dataMount) => {
      const [data, mountPath, ...rest] = dataMount.split(":");
      // we don't currently support source paths, but this future-proofs it
      const [dataId, ...sourcePath] = data.split("/");
      const identifier = dataInstances.get(dataId)?.fullName ?? dataId;
      return [[identifier, ...sourcePath].join("/"), mountPath, ...rest].join(
        ":",
      );
    }),
    command: command
      ? parseCommand(command).parsedCommand.map((arg) =>
          typeof arg === "string"
            ? arg
            : "pattern" in arg
              ? arg.pattern
              : "op" in arg
                ? arg.op
                : arg.comment,
        )
      : undefined,
    name: name || randomlyGeneratedName,
    // Always explicit; see DEFAULT_ACCESS_ISOLATION.
    internalAccessIsolation: resolveAccessIsolation(tentativePayload),
  };
  for (const [key, value] of Object.entries(jobSpec)) {
    if (value === undefined || (value instanceof Array && !value.length)) {
      delete jobSpec[key as keyof JobSpecPost];
    }
  }

  // Serialize internalDns to wire-format options["internal-dns"]. Empty port
  // rows live in Redux while the user edits — filter them here. Backend
  // rejects half-filled sections, so only emit when name + ≥1 port both hold.
  if (internalDns) {
    const validPorts = internalDns.ports
      .filter((p) => p.port !== "" && p.port !== undefined)
      .map((p) => {
        const out: Record<string, unknown> = { port: p.port };
        if (p.targetPort !== "" && p.targetPort !== undefined) {
          out["target-port"] = p.targetPort;
        }
        if (p.protocol) out.protocol = p.protocol;
        return out;
      });
    if (internalDns.name && validPorts.length > 0) {
      const existingOptions = jobSpec.options as
        | Record<string, unknown>
        | undefined;
      jobSpec.options = {
        ...existingOptions,
        "internal-dns": { name: internalDns.name, ports: validPorts },
      };
    }
  }

  return jobSpec;
}

export function getRandomName() {
  return generateFriendlyName();
}

export function incrementJobName(name: string) {
  return name.replace(/(?:_(\d+))?$/, (_, n) => `_${n ? Number(n) + 1 : 1}`);
}

type AllowedJobSpecProperties = {
  [Key in keyof Required<JobSpec>]: NonNullable<JobSpec[Key]> extends
    | Array<unknown>
    | Record<string, unknown>
    ? boolean
    : NonNullable<JobSpec[Key]> extends object
      ? Record<keyof NonNullable<JobSpec[Key]>, boolean>
      : boolean;
};
const allowedJobSpecProperties: AllowedJobSpecProperties = {
  image: true,
  data: true,
  environmentVars: true,
  workdir: true,
  command: true,
  resources: {
    cpu: true,
    mem: true,
    gpu: true,
    gpuMem: true,
    replicas: true,
    gpuModel: true,
    // below resources properties are allowed but not editable in job form
    // TODO: fully support in job form
    cpuModel: true,
    gpuTensorCores: true,
    gpuCudaVersion: true,
    inference: true,
  },
  preemptable: true,
  restartable: true,
  name: true,
  internalAccessIsolation: true,
  // below JobSpec properties are allowed but not editable in job form
  // TODO: fully support in job form
  isProcessAgent: true,
  role: true,
  scopes: true,
  bid: true,
  maxRunTime: true,
  tags: true,
  options: true,
  // 'interactive' will be stripped out by the job form
  // TODO: support or make dedicated UI for interactive
  interactive: false,
  // 'owner' should not be copied when duplicating — new job belongs to the current user
  owner: false,
  replicaFailuresAllowed: true,
};

export function jobToTentativePayload(job: Job) {
  const tentativePayload: TentativeJobSpec = {
    randomlyGeneratedName: getRandomName(),
  };
  for (const [key, value] of Object.entries(job)) {
    const allowed = allowedJobSpecProperties[key as keyof JobSpec];
    if (!allowed) {
      continue;
    }
    if (key === "name") {
      tentativePayload[key] = incrementJobName(value);
    } else if (key === "command") {
      tentativePayload[key] = shellQuote.quote(value as string[]);
    } else if (key === "options") {
      const rawOptions = value as Record<string, unknown> | null | undefined;
      if (rawOptions && typeof rawOptions === "object") {
        // Lift internal-dns out of the generic options map into its own
        // typed DnsConfig field; other options pass through unchanged.
        const { "internal-dns": rawDns, ...optionsWithoutDns } = rawOptions as {
          "internal-dns"?: {
            name?: string;
            ports?: Array<{
              port?: number;
              "target-port"?: number;
              protocol?: string;
            }>;
          };
          [k: string]: unknown;
        };
        if (Object.keys(optionsWithoutDns).length) {
          tentativePayload.options = optionsWithoutDns;
        }
        if (rawDns) {
          const rawName = rawDns.name ?? "";
          tentativePayload.internalDns = {
            name: stripDnsPrefix(rawName, job.resources?.replicas),
            ports: (rawDns.ports ?? []).map((p) => ({
              port: typeof p.port === "number" ? p.port : "",
              targetPort:
                typeof p["target-port"] === "number" ? p["target-port"] : "",
              protocol:
                p.protocol === "TCP" || p.protocol === "UDP"
                  ? p.protocol
                  : undefined,
            })),
          };
        }
      }
    } else if (key === "environmentVars") {
      const vars: string[] = value;
      tentativePayload[key] = vars.map((v) => {
        const equalsIndex = v.indexOf("=");
        const varName = v.slice(0, equalsIndex);
        const varValue = v.slice(equalsIndex + 1);
        return `${varName}=${varValue}`;
      });
    } else if (key === "data") {
      tentativePayload[key] = value as string[];
    } else if (typeof allowed === "object") {
      const val: typeof value = {};
      for (const [k, v] of Object.entries(value)) {
        if (!allowed[k as keyof typeof allowed]) {
          continue;
        }
        val[k] = v;
      }
      if (Object.keys(val).length) {
        tentativePayload[key as keyof JobSpec] = val;
      }
    } else {
      tentativePayload[key as keyof JobSpec] = value;
    }
  }
  return tentativePayload;
}

// https://stackoverflow.com/a/57364353/4956731
export type Await<T> = T extends {
  then(onfulfilled?: (value: infer U) => unknown): unknown;
}
  ? U
  : T;

const { hostname, protocol } = window.location;
// Removing sub-domain "jobs." or "job-id-or-full-name.job." if present
const consoleHost = hostname.replace(/^jobs\.|^[^.]+\.job\./, "");
const proxyHost = "localhost:8080";
const useConsoleHost = process.env.NODE_ENV !== "development";
// The maximum length of each domain label is 63 characters.
const DOMAIN_LABEL_MAX_LEN = 63;

// Encode a full name to be URL safe by replacing `_` → `--`, then `.` → `-`.
// The server decodes this in reverse (-- → _, - → .) in ParseJobIDandPort.
// Resource names only allow [a-z0-9_] and fullNames are dot-separated
// (e.g. org.account.jobname), so `--` never appears in valid input and
// the encoding is invertible for all real names.
function encodeFullNameForUrl(fullName: string) {
  return fullName.replace(/_/g, "--").replace(/\./g, "-");
}

export function getJobWebAccessUrl(job: Job) {
  const encoded = job.fullName && encodeFullNameForUrl(job.fullName);
  const urlPart =
    encoded && encoded.length <= DOMAIN_LABEL_MAX_LEN ? encoded : job.id;
  return useConsoleHost
    ? `${protocol}//${urlPart}.job.${consoleHost}`
    : `${protocol}//${proxyHost}/job/${urlPart}/`;
}

export function getJobResourceUsageUrl(job: Job) {
  const pathname = `/to/resource_url/${job.id}?full`;
  return useConsoleHost
    ? `${protocol}//graphs.${consoleHost}${pathname}`
    : `${protocol}//${proxyHost}/subdomain/graphs${pathname}`;
}

export function jobStateDescription(state: JobState): string {
  switch (state) {
    case JobState.QUEUING:
      return "Job waiting for resources";
    case JobState.QUEUED:
      return "Job will start soon";
    case JobState.RUNNING:
      return "Job is now running";
    case JobState.SUCCEEDED:
      return "Job finished successfully";
    case JobState.FAILED:
      return "Job failed";
    case JobState.INTERRUPTED:
      return "Job was interrupted";
    case JobState.CANCELLED:
      return "Job was cancelled";
    case JobState.CANCELLING:
      return "Job is cancelling";
  }
}

export function assertIsError(error: unknown): asserts error is Error {
  if (!(error instanceof Error)) {
    throw error;
  }
}

export const MAX_ENV_FILE_BYTES = 64 * 1024;
export const MAX_ENV_VAR_COUNT = 200;

const SECRET_KEY_RE = /KEY|TOKEN|SECRET|PASSWORD/i;

export function isSecretKey(name: string): boolean {
  return SECRET_KEY_RE.test(name);
}

// ENV_KEY_RE is the environment-variable name charset enforced by the backend
// (normalize_job_spec.go's envRegex name portion): a leading letter/-/./_
// (never a digit) then letters, digits, -, ., or _.
const ENV_KEY_RE = /^[-._a-zA-Z][-._a-zA-Z0-9]*$/;

/**
 * Parse .env file content into a key/value map. This is a deliberately small
 * subset shared verbatim with the CLI (see the shared envfile_spec.json
 * fixture); the Go implementation in eai-cli needs to stay in sync.
 *
 *   - KEY=VALUE; the key is everything before the first '='.
 *   - Keys are kept literal and validated against the backend charset. A line
 *     with '=' but an invalid key throws; a line with no '=' is skipped.
 *   - Values may be single- or double-quoted; quotes are stripped and the
 *     content taken verbatim, including real newlines (multi-line values). An
 *     unterminated quote throws. No escape sequences are processed.
 *   - Unquoted values are single-line, trimmed, with a trailing " # comment"
 *     (a '#' preceded by whitespace) removed.
 *   - An optional `export ` prefix is stripped (shell-sourceable files).
 *   - Blank lines and full-line '#' comments are skipped.
 *   - No variable substitution, no backtick quoting, no colon-form. Duplicate
 *     keys keep their first position with the last value.
 */
export function parseEnvFile(content: string): Record<string, string> {
  const lines = content.replace(/\r\n?/g, "\n").split("\n");
  const out: Record<string, string> = {};

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].replace(/^[ \t]+/, "");
    if (line === "" || line.startsWith("#")) continue;

    // Strip an optional `export ` prefix (shell-sourceable .env files).
    const exp = /^export[ \t]+/.exec(line);
    if (exp) line = line.slice(exp[0].length);

    const eq = line.indexOf("=");
    if (eq < 0) continue; // no '=': a stray word, not a statement

    const key = line.slice(0, eq).trim();
    if (!ENV_KEY_RE.test(key)) {
      throw new Error(`invalid environment variable name: "${key}"`);
    }

    const rest = line.slice(eq + 1);
    const afterWS = rest.replace(/^[ \t]+/, "");

    if (afterWS.length > 0 && (afterWS[0] === '"' || afterWS[0] === "'")) {
      const quote = afterWS[0];
      const body = afterWS.slice(1);
      const closeIdx = body.indexOf(quote);
      if (closeIdx >= 0) {
        out[key] = body.slice(0, closeIdx);
        continue;
      }
      // Multi-line value: consume following lines until the closing quote.
      let buf = body;
      let closed = false;
      for (let j = i + 1; j < lines.length; j++) {
        buf += "\n";
        const idx = lines[j].indexOf(quote);
        if (idx >= 0) {
          buf += lines[j].slice(0, idx);
          i = j;
          closed = true;
          break;
        }
        buf += lines[j];
      }
      if (!closed) {
        throw new Error(`unterminated quoted value for "${key}"`);
      }
      out[key] = buf;
      continue;
    }

    out[key] = stripInlineComment(rest).trim();
  }

  return out;
}

// stripInlineComment removes a trailing comment: everything from the first '#'
// preceded by whitespace. A '#' not preceded by whitespace stays literal.
function stripInlineComment(s: string): string {
  for (let i = 1; i < s.length; i++) {
    if (s[i] === "#" && (s[i - 1] === " " || s[i - 1] === "\t")) {
      return s.slice(0, i);
    }
  }
  return s;
}

// envValueNeedsQuoting reports whether a value can't be written bare without the
// parser misreading it (newlines, edge whitespace, a leading quote char, or a
// " #" that would look like a trailing comment).
function envValueNeedsQuoting(value: string): boolean {
  return (
    value.includes("\n") ||
    value.includes("\r") ||
    value !== value.trim() ||
    value.startsWith('"') ||
    value.startsWith("'") ||
    / #/.test(value)
  );
}

/**
 * Serialize a KEY=VALUE string array back to .env file content, round-trippable
 * through parseEnvFile. Since the parser does no unescaping, values are quoted
 * (with a quote char the value doesn't contain) rather than escaped, and real
 * newlines are written verbatim inside the quotes. Throws if a value needs
 * quoting but contains both quote characters, since no quote char is then safe
 * to delimit it with (silently picking one would truncate the value on the
 * next parse).
 */
export function toEnvFile(envVars: string[]): string {
  const lines = envVars.map((raw) => {
    const eqIdx = raw.indexOf("=");
    const key = eqIdx >= 0 ? raw.slice(0, eqIdx) : raw;
    const value = eqIdx >= 0 ? raw.slice(eqIdx + 1) : "";
    if (!envValueNeedsQuoting(value)) return `${key}=${value}`;
    if (value.includes('"') && value.includes("'")) {
      throw new Error(
        `cannot write "${key}": its value contains both ' and " and needs quoting, which isn't supported`,
      );
    }
    // No escaping is supported, so pick a quote the value doesn't contain.
    const quote = value.includes('"') ? "'" : '"';
    return `${key}=${quote}${value}${quote}`;
  });
  return lines.join("\n") + (lines.length > 0 ? "\n" : "");
}
